import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { clearCartItems } from '../features/cart/cartSlice';
import { Button } from '../components/ui';
import { devLog, devError, sanitizeForLog } from '../utils/logger';

// Helper pour générer les URL d'API
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://website-alturion-production.up.railway.app' 
    : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const isValidId = (id) => {
  return typeof id === 'string' && /^[a-zA-Z0-9_-]+$/.test(id);
};

const clearCart = (dispatch) => {
  try {
    dispatch(clearCartItems());
    localStorage.removeItem('cartItems');
    devLog('✅ Panier vidé avec succès');
  } catch (error) {
    devError('❌ Erreur lors du vidage du panier:', sanitizeForLog(error));
  }
};

const CheckoutSuccessScreen = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [redirectProgress, setRedirectProgress] = useState(0);
  const [redirectTime] = useState(5);
  const [startTime] = useState(Date.now());
  const [cartCleared, setCartCleared] = useState(false);
  
  const { userInfo } = useSelector(state => state.auth);
  
  const params = new URLSearchParams(location.search);
  const sessionId = params.get('session_id');
  const orderId = params.get('order_id');
  const isFree = params.get('free') === 'true';
  
  useEffect(() => {
    if (orderInfo?.paid && !cartCleared) {
      clearCart(dispatch);
      setCartCleared(true);
    }
  }, [orderInfo, cartCleared, dispatch]);
  
  useEffect(() => {
    if (orderInfo && orderInfo.paid) {
      const interval = setInterval(() => {
        setRedirectProgress(prev => {
          const newProgress = prev + (100 / (redirectTime * 10));
          if (newProgress >= 100) {
            clearInterval(interval);
            navigate('/profile?tab=orders&newpurchase=true');
            return 100;
          }
          return newProgress;
        });
      }, 100);
      
      return () => clearInterval(interval);
    }
  }, [orderInfo, navigate, redirectTime]);
  
  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
      return;
    }
    
    if (!sessionId && !orderId) {
      navigate('/');
      return;
    }

    if (isFree && orderId) {
      setOrderInfo({
        paid: true,
        order: orderId,
      });
      setLoading(false);
      return;
    }

    if ((sessionId && !isValidId(sessionId)) || (orderId && !isValidId(orderId))) {
      setError(t('checkoutSuccess.errors.invalidId'));
      setLoading(false);
      return;
    }
    
    const checkPaymentStatus = async () => {
      try {
        setLoading(true);
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        };
        
        if (sessionId) {
          try {
            const sessionResponse = await axios.get(
              getApiUrl(`/api/checkout/sessions/${sessionId}?order_id=${orderId}`),
              config
            );
            
            if (sessionResponse.data && sessionResponse.data.paid) {
              setOrderInfo({
                paid: true,
                order: orderId,
              });
              setLoading(false);
              return;
            }
          } catch (sessionError) {
            devError('Erreur lors de la vérification de la session:', sanitizeForLog(sessionError));
          }
        }
        
        if (orderId) {
          try {
            const orderResponse = await axios.get(
              getApiUrl(`/api/orders/${orderId}`),
              config
            );
            
            if (orderResponse.data && orderResponse.data._id) {
              if (orderResponse.data.user !== userInfo._id) {
                localStorage.removeItem('userInfo');
                navigate('/login?error=session_mismatch');
                return;
              }
              
              if (orderResponse.data.isPaid) {
                setOrderInfo({
                  paid: true,
                  order: orderResponse.data._id,
                });
              } else {
                setTimeout(() => {
                  const elapsedTime = Date.now() - startTime;
                  if (elapsedTime < 30000) {
                    checkPaymentStatus();
                  } else {
                    setError(t('checkoutSuccess.errors.timeout'));
                    setLoading(false);
                  }
                }, 2000);
              }
              setLoading(false);
              return;
            }
          } catch (orderError) {
            devError('Erreur lors de la vérification de la commande:', sanitizeForLog(orderError));
            setError(t('checkoutSuccess.errors.orderVerification'));
            setLoading(false);
            return;
          }
        }
        
        setError(t('checkoutSuccess.errors.paymentVerification'));
        setLoading(false);
      } catch (err) {
        devError('Erreur lors de la vérification du paiement:', sanitizeForLog(err));
        setError(t('checkoutSuccess.errors.general'));
        setLoading(false);
      }
    };
    
    checkPaymentStatus();
  }, [sessionId, orderId, isFree, userInfo, navigate, dispatch, startTime, t]);
  
  const cancelRedirect = () => {
    setRedirectProgress(0);
  };
  
  const redirectNow = () => {
    navigate('/profile?tab=orders&newpurchase=true');
  };

  const nextSteps = [
    t('checkoutSuccess.indicatorsAvailable'),
    t('checkoutSuccess.emailSent'),
  ];
  
  return (
    <div className="min-h-screen bg-bg px-6 pt-28 pb-24 md:pt-32">
      <div className="mx-auto max-w-xl">
        {loading ? (
          <div className="border-y border-white/[.06] py-16 text-center">
            <Loader />
            <p className="mt-4 text-sm text-muted">{t('checkoutSuccess.verifying')}</p>
          </div>
        ) : error ? (
          <div className="border-y border-white/[.06] py-10">
            <Message variant="danger">{error}</Message>
            <Button to="/cart" variant="secondary" className="mt-6">
              {t('checkoutSuccess.backToCart')}
            </Button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-muted">
              Checkout
            </p>
            <div className="mb-8 flex items-start gap-4">
              <CheckCircle2
                className="mt-1 h-6 w-6 shrink-0 text-body"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <div>
                <h1 className="text-[28px] font-semibold tracking-tight text-body md:text-[36px]">
                  {t('checkoutSuccess.paymentAccepted')}
                </h1>
                <p className="mt-2 text-base text-muted">{t('checkoutSuccess.thankYou')}</p>
              </div>
            </div>

            <p className="mb-8 text-sm leading-relaxed text-muted">
              {t('checkoutSuccess.orderProcessed')}
            </p>

            {orderInfo?.order && (
              <div className="mb-8 flex items-baseline justify-between gap-4 border-y border-white/[.06] py-4">
                <span className="text-sm text-muted">{t('checkoutSuccess.orderNumber')}</span>
                <span className="font-mono text-sm font-medium text-body">{orderInfo.order}</span>
              </div>
            )}

            <ul className="mb-8 divide-y divide-white/[.06] border-y border-white/[.06]">
              {nextSteps.map((step) => (
                <li key={step} className="py-4 text-sm leading-relaxed text-muted">
                  {step}
                </li>
              ))}
              <li className="py-4">
                <p className="text-sm font-medium text-body">
                  {t('checkoutSuccess.findOrder.title')}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-muted">
                  {t('checkoutSuccess.findOrder.description')}
                </p>
              </li>
            </ul>

            {redirectProgress > 0 && (
              <div className="mb-8">
                <p className="mb-3 text-sm text-muted">
                  {t('checkoutSuccess.redirecting', {
                    seconds: Math.ceil(redirectTime - (redirectProgress * redirectTime) / 100),
                  })}
                </p>
                <div className="h-px w-full overflow-hidden bg-white/[.08]">
                  <div
                    className="h-full bg-body transition-all duration-300 ease-out"
                    style={{ width: `${redirectProgress}%` }}
                  />
                </div>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Button variant="secondary" className="flex-1" onClick={cancelRedirect}>
                    {t('checkoutSuccess.stayOnPage')}
                  </Button>
                  <Button className="flex-1" onClick={redirectNow}>
                    {t('checkoutSuccess.viewOrders')}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            )}

            {!redirectProgress && (
              <Button className="w-full" onClick={redirectNow}>
                {t('checkoutSuccess.viewOrders')}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccessScreen;
